import { useCallback, useMemo } from 'react';

type KeyEventType = 'keydown' | 'keyup' | 'keypress';

type KeyListenerCallback = (e: KeyboardEvent) => void;

type RegisteredKeybinding = {
  id: string;
  key: string;
  event: KeyEventType;
  callback: KeyListenerCallback;
};

let registered: RegisteredKeybinding[] = [];

/**
 * 전역 단축키를 실행해도 되는지 판단하기 위한 입력 문맥 검사기.
 * 일반 input/textarea뿐 아니라 contentEditable 기반 에디터(예: Lexical)도
 * 텍스트 입력 컨텍스트로 간주해야 하므로 조상의 contentEditable까지 포함해 판별한다.
 *
 * @param element 검사 대상 DOM 노드
 * @returns 텍스트 입력 문맥이면 true
 */
export const isInputLikeElement = (
  element: EventTarget | Element | null,
): boolean => {
  if (!(element instanceof Element)) {
    return false;
  }

  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
  ) {
    return true;
  }

  if (element instanceof HTMLElement && element.isContentEditable) {
    return true;
  }

  return Boolean(
    element.closest('[contenteditable]:not([contenteditable="false"])'),
  );
};

const regKeybinding = (binding: RegisteredKeybinding) => {
  registered = [...registered, binding];
  window.addEventListener(binding.event, binding.callback);
};

const unregisterKeybinding = (binding: RegisteredKeybinding) => {
  registered = registered.filter((r) => {
    if (r.id === binding.id) {
      window.removeEventListener(binding.event, binding.callback);

      return false;
    }

    return true;
  });
};

export const useKeybinding = () => {
  const registerKeybinding = useCallback(
    (options: {
      event: KeyEventType;
      key: string;
      commandCtrlKey: boolean;
      callback: (e: KeyboardEvent) => void;
      preventDefault: boolean;
      triggerIfInputFieldFocused: boolean;
    }) => {
      const listener = (e: KeyboardEvent) => {
        const commandKey = window.navigator.platform.startsWith('Mac')
          ? e.metaKey
          : e.ctrlKey;
        if (
          e.key.toLowerCase() === options.key.toLowerCase() &&
          options.commandCtrlKey === commandKey
        ) {
          if (!options.triggerIfInputFieldFocused) {
            /**
             * 일부 브라우저/에디터 조합에서는 activeElement와 이벤트 target이 다를 수 있다.
             * 둘 중 하나라도 입력 문맥이면 단축키를 무시해 문자 입력을 우선한다.
             */
            const { activeElement } = document;
            if (
              isInputLikeElement(activeElement) ||
              isInputLikeElement(e.target)
            ) {
              return;
            }
          }

          options.callback(e);
          if (options.preventDefault) {
            e.preventDefault();
          }
        }
      };

      const toRegister: RegisteredKeybinding = {
        event: options.event,
        key: options.key,
        callback: listener,
        id: String(Math.random()),
      };

      regKeybinding(toRegister);
      return {
        unregister: () => unregisterKeybinding(toRegister),
      };
    },
    [],
  );

  return useMemo(() => ({ registerKeybinding }), [registerKeybinding]);
};
